import { useCallback, useEffect, useMemo } from 'react';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { useUpdatePurchasesPermissions } from 'src/components/Purchases/BulkActions/useUpdatePurchasesPermissions';
import { LaunchBulkMarkPurchaseAsPaid } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkMarkPurchaseAsPaid/LaunchBulkMarkPurchaseAsPaid';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { InfoMessage } from 'src/core/POS/MessageWithIcon/InfoMessage';
import { BulkMarkPurchaseAsPaidDialog } from 'src/dialogs/BulkEdits/BulkMarkPurchaseAsPaidDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditPreviewWithDetails,
  BulkEditPurchaseClient,
  CommandOptionType,
  PurchaseOrderQuery,
} from 'src/WebApiController';

import { PURCHASE_BULK_MARK_AS_PAID_KEY } from '../../PurchaseActionDropdown.constants';

export const useBulkMarkPurchaseAsPaid = (
  filterQuery: PurchaseOrderQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  groupId?: string,
  isEventView?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasUpdatePurchasesPermission } = useUpdatePurchasesPermissions();

  const updateKey = groupId
    ? `bulk-mark-purchase-as-paid-event-${groupId}`
    : PURCHASE_BULK_MARK_AS_PAID_KEY;

  const { showErrorDialog } = useClientLogContext();
  const { refreshData } = usePurchaseDataContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const {
    openDialog: openBulkMarkPurchaseAsPaidDialog,
    closeDialog: closeBulkMarkPurchaseAsPaidDialog,
  } = useDialog(DialogId.BulkMarkPurchaseAsPaid, BulkMarkPurchaseAsPaidDialog);

  const updatePurchaseOrders = !isEventView;

  const onSubmit = useCallback(
    async (
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const bulkEditClient = new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            );

            const preview =
              await bulkEditClient.bulkMarkPurchaseAsPaidPreview(filterQuery);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkMarkPurchaseAsPaidPreview', error, {
              trackErrorData: { filterQuery },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const bulkEditClient = new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            );

            const succeeded = await bulkEditClient.bulkMarkPurchaseAsPaid(
              preview!.preview,
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                await refreshData(true);
                await refreshExpandedListItems();
              }

              closeBulkMarkPurchaseAsPaidDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkMarkPurchaseAsPaid', error, {
              trackErrorData: {
                preview,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      filterQuery,
      refreshData,
      refreshExpandedListItems,
      setIsLoading,
      showErrorDialog,
      updateKey,
      closeBulkMarkPurchaseAsPaidDialog,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkMarkPurchaseAsPaidDialog({
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      entityType: ActionOutboxEntityType.Purchase,
      infoMessage: (
        <InfoMessage
          message={<Content id={ContentId.BulkMarkPurchaseAsPaid} />}
        />
      ),
      onCancel: closeBulkMarkPurchaseAsPaidDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.PurchasesBulkMarkPurchaseAsPaid,
        onSelect: openDialogStableRef,
      },
      disabled: !hasUpdatePurchasesPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.PurchasesBulkMarkPurchaseAsPaid,
        disabled: !hasUpdatePurchasesPermission,
      });
    };
  }, [
    hasUpdatePurchasesPermission,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkMarkPurchaseAsPaidDialog({
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        entityType: ActionOutboxEntityType.Purchase,
        infoMessage: (
          <InfoMessage
            message={<Content id={ContentId.BulkMarkPurchaseAsPaid} />}
          />
        ),
        onCancel: closeBulkMarkPurchaseAsPaidDialog,
      });
    },
    [
      closeBulkMarkPurchaseAsPaidDialog,
      onSubmit,
      openBulkMarkPurchaseAsPaidDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasUpdatePurchasesPermission) {
      return [];
    }

    return [
      {
        title: ContentId.BulkMarkPurchaseAsPaid,
        onClick: onClick,
        disabled: isLoading,
      },
    ];
  }, [hasUpdatePurchasesPermission, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchBulkMarkPurchaseAsPaid
        key="purchaseBulkMarkAsPaidUpdate"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
